import * as React from 'react'

import { Box, type Colors } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import getContractVersionStyles from 'modules/contracts/components/ContractHistory/lib/getContractVersionStyles'
import type Contract from 'modules/contracts/lib/Contract'
import {
  ContractSchedule,
  getContractVersionSchedule,
} from 'modules/contracts/lib/contractSchedule'
import { useRoute } from 'modules/contracts/pages/ContractVersionsPage/_RouteContext'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'

import CollapsibleContractVersionCard from './CollapsibleContractVersionCard'
import FirstContractVersionCard from './FirstContractVersionCard'

interface Props {
  contractVersion: ContractVersion
  contractVersionIndex: number
  contractContractVersions: ContractVersion[]
  allContractVersionsSorted: ContractVersion[]
  previousContract?: Contract
  highlightedItem?: ContractVersion
  setSelectedContractVersion: (item: ContractVersion | undefined) => void
}

interface CardTypeProps {
  contractVersion: ContractVersion
  contractVersionIndex: number
  contractContractVersions: ContractVersion[]
  contractVersionSchedule: string
  isHighlighted: boolean
  showChanges: boolean
  hasPreviousContract: boolean
}

const CardType = ({
  contractVersion,
  contractVersionIndex,
  contractContractVersions,
  contractVersionSchedule,
  isHighlighted,
  showChanges,
  hasPreviousContract,
}: CardTypeProps) => {
  if (contractVersionIndex > 1) {
    return (
      <CollapsibleContractVersionCard
        contractVersion={contractVersion}
        sortedContractVersions={contractContractVersions}
        contractVersionSchedule={contractVersionSchedule}
        isHighlighted={isHighlighted}
        showChanges={showChanges}
      />
    )
  }

  return (
    <FirstContractVersionCard
      contractVersion={contractVersion}
      contractVersionSchedule={contractVersionSchedule}
      isHighlighted={isHighlighted}
      showChanges={showChanges}
      hasPreviousContract={hasPreviousContract}
    />
  )
}

const ContractVersionCard = ({
  contractVersion,
  contractVersionIndex,
  contractContractVersions,
  allContractVersionsSorted,
  previousContract,
  highlightedItem,
  setSelectedContractVersion,
}: Props) => {
  const route = useRoute()
  const [isHovered, setIsHovered] = React.useState(false)
  const contractVersionSchedule = getContractVersionSchedule(
    contractVersion,
    allContractVersionsSorted
  )
  const isHighlighted = highlightedItem?.id === contractVersion.id
  const [expandChanges, toggleExpandChanges] = React.useState<boolean>(
    contractVersionSchedule === ContractSchedule.ongoing || isHighlighted
  )
  const { background, borderStyle, borderColor } = getContractVersionStyles(
    contractVersionSchedule,
    isHovered,
    isHighlighted
  )

  const onContractVersionSelected = (contractVersion: ContractVersion) => {
    const showChanges = isHighlighted ? !expandChanges : true
    window.history.replaceState(
      null,
      'Factorial',
      route.contractVersionPath(contractVersion.attributes.id)
    )
    toggleExpandChanges(showChanges)
    setSelectedContractVersion(contractVersion)
    track(trackingEvents.employee.contracts.selectTimelineContractConditions, {
      contract_status: contractVersionSchedule,
    })
  }

  return (
    <Box
      paddingX="s16"
      paddingTop="s16"
      paddingBottom="s16"
      background={background as Colors}
      gap="s24"
      width="full"
      cursor="pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onContractVersionSelected(contractVersion)}
      borderRadius={{ all: 'abs008' }}
      border={{
        all: {
          width: 's1',
          style: borderStyle,
          color: borderColor,
        },
      }}
      style={{ position: 'relative' }}
    >
      <CardType
        contractVersion={contractVersion}
        contractVersionIndex={contractVersionIndex}
        contractContractVersions={contractContractVersions}
        contractVersionSchedule={contractVersionSchedule}
        isHighlighted={isHighlighted}
        showChanges={expandChanges}
        hasPreviousContract={!!previousContract}
      />
    </Box>
  )
}

export default observer(ContractVersionCard)
